"use client";

import {
    Character,
    CharacterContent,
    ConsumerMedia,
    ConsumerMediaCharacter,
    ConsumerMediaCover,
    ConsumerMediaGroup,
    Media,
} from "@/src/payload-types";
import React, { ReactElement, ReactNode } from "react";
import { useIsMobile } from "@/src/blocks/converters";
import useSWR from "swr";
import getConsumerMediaByGroup from "@/src/utils/getConsumerMediaGroup";
import { BookOpen, Calendar, Clock, List, Mic, PenTool } from "lucide-react";
import { FaAmazon, FaAudible, FaShoppingCart, FaSteam } from "react-icons/fa";
import { LINKTYPES } from "@/src/types";
import Link from "next/link";
import { getBlobUrl, getCharacterLink } from "@/src/components/utils/utils";
import { SheetContainer } from "@/src/components/Containers";
import RelationLink from "@/src/components/RelationLink";

enum ConsumerMediaTabs {
    SUMMARY = "SUMMARY",
    CHARACTERS = "CHARACTERS",
}

const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const BuyLink = ({ type, link }: { type?: keyof typeof LINKTYPES; link?: string }) => {
    const { icon, text, service } = (() => {
        let icon, text, service;
        switch (type) {
            case "AMAZON":
                icon = <FaAmazon size={36} className="text-[#ff9900]" />;
                text = "Buy on";
                service = "Amazon";
                break;
            case "AUDIBLE":
                icon = <FaAudible size={36} className="text-[#ffa000]" />;
                text = "Listen on";
                service = "Audible";
                break;
            case "STEAM":
                icon = <FaSteam size={36} className="text-[#232730 ]" />;
                text = "Buy on";
                service = "Steam";
                break;
            case "OTHER":
            default:
                icon = <FaShoppingCart size={48} className="text-gray-400" />;
                text = "Buy on";
                service = "Store";
                break;
        }
        return { text, icon, service };
    })();

    const formatUrl = () => {
        if (!link) return "";
        let typeString = "";
        if (link.includes("?=")) {
            typeString = `&type=${type}`;
        } else {
            typeString = `?type=${type}`;
        }
        return `/r/${encodeURIComponent(link + typeString)}`;
    };

    return (
        <Link
            href={formatUrl()}
            className="flex no-underline items-center gap-2 border border-gray-400 w-fit h-12.5 pl-2 pr-2 rounded-xl hover:cursor-pointer hover:bg-gray-900 transition-all"
        >
            <div>{icon}</div>
            <div className="flex flex-col *:leading-4 text-left">
                <span className="text-sm text-gray-300">{text}</span>
                <span className="text-xl ">{service}</span>
            </div>
        </Link>
    );
};

const MediaProperties = ({ consumerMedia }: { consumerMedia: ConsumerMedia }) => {
    const properties = [];

    switch (consumerMedia.type) {
        case "BOOK":
            if (consumerMedia.bookProperties?.author) {
                properties.push(
                    <InfoPoint
                        key="AUTHOR"
                        title="AUTHOR"
                        value={consumerMedia.bookProperties?.author}
                        icon={<PenTool />}
                    />,
                );
            }
            if (consumerMedia.bookProperties?.narrator) {
                properties.push(
                    <InfoPoint
                        key="NARRATOR"
                        title="NARRATOR"
                        value={consumerMedia.bookProperties?.narrator}
                        icon={<Mic />}
                    />,
                );
            }
            if (consumerMedia.bookProperties?.pages) {
                properties.push(
                    <InfoPoint
                        key="PAGES"
                        title="PAGES"
                        value={consumerMedia.bookProperties?.pages?.toString()}
                        icon={<BookOpen />}
                    />,
                );
            }
            if (consumerMedia.bookProperties?.length) {
                properties.push(
                    <InfoPoint
                        key="DURATION"
                        title="DURATION"
                        value={consumerMedia.bookProperties?.length}
                        icon={<Clock />}
                    />,
                );
            }
            break;
        case "GAME":
            break;
        case "SHOW":
            break;
        case "MOVIE":
            break;
    }

    if (consumerMedia.releaseDate) {
        properties.push(
            <InfoPoint
                key="RELEASED"
                title="RELEASED"
                value={`${months[new Date(consumerMedia.releaseDate).getMonth()]} ${new Date(consumerMedia.releaseDate).getFullYear()}`}
                icon={<Calendar />}
            />,
        );
    }

    return <>{properties}</>;
};

const ConsumerMediaDetail = ({
    consumerMedia,
    consumerMediaCharacters,
    inSheet,
}: {
    consumerMedia: ConsumerMedia;
    consumerMediaCharacters: ConsumerMediaCharacter[];
    onCharacterSelect?: (val: Character) => void;
    inSheet: boolean;
}) => {
    const splash = (consumerMedia.cover as ConsumerMediaCover)?.sizes?.cover;
    const image = splash && getBlobUrl(splash.url!);

    // useEffect(() => {
    //     const handleMessage = async (event: MessageEvent) => {
    //         // Only accept messages from Payload admin
    //         if (event.origin !== "http://localhost:3000") return;
    //         if (event.data?.type === "payload-live-preview") {
    //             const tempContent = content.map((c) => (c.id === event.data.data.id ? event.data.data : c));
    //             // setPreviewContent(sortContent(tempContent));
    //         }
    //     };
    //
    //     window.addEventListener("message", handleMessage);
    //     if (content.length) {
    //         setTab(content[0].id.toString());
    //     }
    //     return () => window.removeEventListener("message", handleMessage);
    // }, [content]);
    //
    function getContentComponent(c: keyof typeof ConsumerMediaTabs) {
        switch (c) {
            case "SUMMARY":
                return <ContentSummary consumerMedia={consumerMedia} />;
            case "CHARACTERS":
                return <div>chars</div>;
            // return (
            //     <ContentRelationships
            //         character={character}
            //         content={c}
            //         connections={connections}
            //         onCharacterSelect={onCharacterSelect}
            //     />
            // );
        }
    }

    // const contentToUse = sortContent(previewContent || content);

    return (
        <SheetContainer inSheet={inSheet} img={image}>
            <Link className="z-20 mt-6 pl-8 mb-[-22px]" href={"/"}>
                Back to web
            </Link>
            <div className="p-0 pr-8 pl-8 pt-0 shrink-0">
                <ConsumerMediaHeader consumerMedia={consumerMedia} splash={splash} image={image} />
            </div>
            <div className="border border-gray w-19/20 m-auto mt-2 mb-0" />
            <div className="relative flex flex-col justify-center p-8 pt-0">
                <div>
                    <h2>Summary</h2>
                    <ContentSummary consumerMedia={consumerMedia} />{" "}
                </div>{" "}
                <div>
                    <h2>Characters</h2>
                    <ConsumerMediaCharacters
                        characters={consumerMediaCharacters}
                        onCharacterSelect={console.log}
                    />{" "}
                </div>
            </div>
        </SheetContainer>
    );
};

interface InfoPointProps {
    title?: string;
    value?: ReactNode;
    icon?: ReactElement;
}

export const InfoPoint = ({ title, value, icon }: InfoPointProps) => (
    <div className="flex items-start gap-3">
        <span className="text-gray-400 p-1 mt-1">{icon}</span>
        <div className="flex flex-col leading-6">
            <span className="text-gray-400 uppercase tracking-wider pb-[-2px]">{title}</span>
            <span>{value}</span>
        </div>
    </div>
);

interface ConsumerMediaHeaderProps {
    consumerMedia: ConsumerMedia;
    splash?:
        | {
              url?: string | null;
              width?: number | null;
              height?: number | null;
              mimeType?: string | null;
              filesize?: number | null;
              filename?: string | null;
          }
        | undefined;
    image: undefined | string;
}

const ConsumerMediaHeader = ({ consumerMedia, splash, image }: ConsumerMediaHeaderProps) => {
    const isSmallScreen = useIsMobile(1050);

    return isSmallScreen ? (
        <MobileHeader consumerMedia={consumerMedia} splash={splash} image={image} />
    ) : (
        <DesktopHeader consumerMedia={consumerMedia} splash={splash} image={image} />
    );
};

const DesktopHeader = ({ consumerMedia, splash, image }: ConsumerMediaHeaderProps) => {
    return (
        <>
            <div className="relative flex items-top">
                {/*cover*/}
                <div className="basis-1/3 max-w-[250px]">
                    {splash && (
                        <img
                            width={200}
                            src={image}
                            alt={(consumerMedia.cover as ConsumerMediaCover).alt}
                            className="rounded-xl"
                        />
                    )}
                </div>
                {/*Info*/}
                <div className="flex flex-col pt-8 pl-4">
                    {/*Title*/}
                    <div className="pb-4">
                        <div className="text-4xl font-bold">{consumerMedia.title}</div>
                        <div className="text-2xl">{consumerMedia.bookProperties?.author}</div>
                    </div>
                    {/*Misc info*/}
                    <div className="flex">
                        <div className="flex flex-wrap basis-2/3 grow *:basis-1/2 *:pb-8">
                            <MediaProperties consumerMedia={consumerMedia} />
                        </div>
                        {/*Part of*/}
                        {consumerMedia.group && (
                            <div className="basis-1/3">
                                <ConsumerMediaSiblings
                                    currentId={consumerMedia.id}
                                    group={consumerMedia.group as ConsumerMediaGroup}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="relative flex gap-2">
                {consumerMedia.links?.map((link) => (
                    <BuyLink key={link.id ?? link.href} type={link.type!} link={link.href!} />
                ))}
            </div>
        </>
    );
};

const MobileHeader = ({ consumerMedia, splash, image }: ConsumerMediaHeaderProps) => {
    return (
        <>
            <div className="relative flex flex-col items-top">
                {/*cover*/}
                <div className="basis-1/3 max-w-[250px]">
                    {splash && (
                        <img
                            width={200}
                            src={image}
                            alt={(consumerMedia.cover as ConsumerMediaCover).alt}
                            className="rounded-xl"
                        />
                    )}
                </div>
                <div className="pb-4">
                    <div className="text-4xl font-bold">{consumerMedia.title}</div>
                    <div className="text-2xl">{consumerMedia.bookProperties?.author}</div>
                </div>
                {/*Info*/}
                <div className="flex flex-col pt-8 pl-0">
                    {/*Misc info*/}
                    <div className="flex">
                        <div className="flex flex-wrap basis-2/3 *:basis-1/2 *:pb-8">
                            <MediaProperties consumerMedia={consumerMedia} />
                        </div>
                        {/*Part of*/}
                        <div className="basis-1/3">
                            <ConsumerMediaSiblings
                                currentId={consumerMedia.id}
                                group={consumerMedia.group as ConsumerMediaGroup}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative flex gap-2">
                {consumerMedia.links?.map((link) => (
                    <BuyLink key={link.id ?? link.href} type={link.type!} link={link.href!} />
                ))}
            </div>
        </>
    );
};

const sortContent = (c: CharacterContent[]) => {
    return c.sort((c) => (c.type === "SUMMARY" ? -1 : 1));
};

type SourceItemType = {
    id: string;
    index: number;
    quote: string | null;
    externalLink: string | null;
    internalLinkId: string | null;
    internalLinkType: string | null;
    internalLinkLabel: string | null;
};

const ContentSummary = ({ consumerMedia }: { consumerMedia: ConsumerMedia }) => {
    return (
        <div>
            <div>{consumerMedia.summary}</div>
        </div>
    );
};

const MediaInfo = ({ consumerMedia }: { consumerMedia: ConsumerMedia }) => {
    const group = consumerMedia.group as ConsumerMediaGroup;
    return (
        <table className="media-info not-prose">
            <tbody>
                <tr>
                    <th>Author</th>
                    <td>David Annandale</td>
                </tr>
                <tr>
                    <th>Performer</th>
                    <td>Toby Longworth</td>
                </tr>
                <tr>
                    <th>Released</th>
                    <td>October 2016</td>
                </tr>
                <tr>
                    <th>Pages</th>
                    <td>240</td>
                </tr>
                <ConsumerMediaSiblings currentId={consumerMedia.id} group={group} />
            </tbody>
        </table>
    );
};

interface ConsumerMediaSiblingsProps {
    group?: ConsumerMediaGroup;
    currentId: number;
}

const ConsumerMediaSiblings = ({ group, currentId }: ConsumerMediaSiblingsProps) => {
    const { data, error, isLoading } = useSWR(
        ["consumerMediaGroup", group?.id],
        () => (group ? getConsumerMediaByGroup(Number(group?.id)) : null),
        {
            keepPreviousData: true,
        },
    );

    if (isLoading)
        return (
            <tr>
                <th>Loading...</th>
            </tr>
        );
    if (error)
        return (
            <tr>
                <th>Error: {error.message}</th>
            </tr>
        );

    return (
        <div className="flex items-start gap-3">
            <span className="text-gray-400 p-1 mt-1">
                <List />
            </span>
            <div className="flex flex-col leading-6">
                <span className="text-gray-400 uppercase tracking-wider pb-[-2px]">PART OF:</span>
                <div className="text-lg">{group?.name}</div>
                {data?.docs
                    .sort((a, b) => ((a.releaseDate || 0) > (b.releaseDate || 0) ? 1 : -1))
                    .map((ms) => (
                        <div key={ms.id} className="text-nowrap *:text-gray-300">
                            {ms.id === currentId && <span>{"> "}</span>}
                            <RelationLink relationTo={"consumerMedia"} id={ms.slug.toString()} label={ms.title} />
                        </div>
                    ))}
                {/*<span>{value}</span>*/}
            </div>
        </div>
    );

    // return (
    //     <tr>
    //         <th className="align-top pt-4">Part of</th>
    //         <td className="align-top pt-2">
    //             <div className="text-right align-top text-xl">{group?.name}</div>
    //             {data?.docs.map((ms) => (
    //                 <div key={ms.id} className="text-right text-nowrap">
    //                     {ms.id === currentId && <span>{"> "}</span>}
    //                     <RelationLink relationTo={"consumerMedia"} id={ms.id.toString()} label={ms.title} />
    //                 </div>
    //             ))}
    //         </td>
    //     </tr>
    // );
};

const SourceItem = ({ item }: { item: SourceItemType }) => {
    const link = item.externalLink ? (
        <a href={item.externalLink}>{item.quote || item.externalLink}</a>
    ) : (
        <span />
        // getRelationLink(item.internalLinkType, item.internalLinkId, item.internalLinkLabel)
    );

    return (
        <div id={`source-${item.id}`}>
            <span>{item.index}. </span>
            <span>{link}</span>
            {item.quote && <span> - {item.quote}</span>}
        </div>
    );
};

const ConsumerMediaCharacters = ({
    characters,
    onCharacterSelect,
}: {
    characters: ConsumerMediaCharacter[];
    onCharacterSelect: (val: ConsumerMediaCharacter) => void;
}) => {
    return (
        <div className="flex flex-col gap-4">
            {characters.map((char) => (
                <ConsumerMediaCharacterRow key={char.id} character={char} onCharacterSelect={onCharacterSelect} />
            ))}
        </div>
    );
};

const ConsumerMediaCharacterRow = ({
    character,
}: {
    character: ConsumerMediaCharacter;
    onCharacterSelect?: (val: ConsumerMediaCharacter) => void;
}) => {
    const char = character.character as Character;
    const charMedia = char.avatar as Media;
    return (
        <div className="flex flex-col">
            <div className="flex">
                <div className="p-1 w-18 min-w-24">
                    {charMedia.sizes?.avatar?.url && (
                        <img
                            className="rounded-[50px]"
                            src={getBlobUrl(charMedia.sizes.avatar.url!)}
                            width={charMedia.sizes.avatar.width ?? undefined}
                            height={charMedia.sizes.avatar.height ?? undefined}
                            loading="lazy"
                            decoding="async"
                            alt={charMedia.alt}
                        />
                    )}
                </div>
                <div className="">
                    <div className="pl-1 text-2xl">{getCharacterLink(char)}</div>
                    <div className="pl-1">{character.summary}</div>
                </div>
            </div>
        </div>
    );
};

export default ConsumerMediaDetail;
